/*
 * The release-note Version field: a fixed `v` the author cannot delete, and a box
 * for the four numbers after it (S42, 2026-10-12).
 *
 * The owner asked for the version to be entered as "v<x>.<y>.<z>.<a>" with the v
 * pre-entered and impossible to delete. Two ways to build that were weighed:
 *
 *   REJECTED - the v inside the input's value, guarded by caret code. Every
 *   guard has a hole: Home then a digit lands before the v; select-all then a
 *   digit replaces it and the next keystroke lands in the wrong place; Backspace
 *   on the v normalises back to the same value, so React restores the DOM without
 *   re-rendering and an effect that was to fix the caret never runs. Each needs
 *   its own patch, and the review of the first design found all three.
 *
 *   CHOSEN - the house SlugPrefix pattern (an aria-hidden span beside the input
 *   inside one row, see SlugPrefix.tsx), drawn here INSIDE the field's border so
 *   the v reads as the first character of the value. The input holds only what
 *   follows the v, so there is no position in it that is "before the v" and no
 *   caret rule at all. The v is a span: not focusable, not selectable as part of
 *   the value, not in the accessible name - the hint under the field, which the
 *   input points at with aria-describedby, says in words that the v is part of
 *   the version.
 *
 * THE VALUE CROSSING THE BOUNDARY IS ALWAYS THE WHOLE VERSION. `value` comes in
 * as e.g. `v1.2.3.4` (or `v` when empty) and every change goes out through the
 * shared normaliseReleaseNoteVersionInput: whitespace and EVERY v or V are
 * removed from what was typed and one lowercase v is put in front. So typing or
 * pasting `v1.2.3.4`, `V1.2.3.4` or `1.2.3.4` all give `v1.2.3.4`, and the page
 * never sees a version without its v. Nothing else is repaired - a comma or a
 * leading zero stays for the grammar to refuse, under the field, in the server's
 * own words (describeReleaseNoteVersionRequirement).
 *
 * maxLength is the column's 40 less the fixed v. A paste is handled by hand
 * rather than left to the browser, because the browser truncates a paste to
 * maxLength BEFORE the change handler sees it: pasting the longest legal version
 * with its v - 40 characters - would silently lose its last digit to the v that
 * is about to be stripped anyway.
 *
 * The error is the page's to decide (the grammar, or a 409 from the server saying
 * another note of the product already has this version), so it is a prop.
 *
 * Its words come from the chosen language's dictionary, `t.admin.releaseNotes` (S48).
 */

import { useLayoutEffect, useRef, type ClipboardEvent } from 'react';
import {
  RELEASE_NOTE_VERSION_MAX_LENGTH,
  RELEASE_NOTE_VERSION_PREFIX,
  normaliseReleaseNoteVersionInput,
} from '@accotool-kb/shared';
import { useMessages } from '../../i18n/language.js';

/** What the box itself may hold: the column width less the fixed prefix. */
const TYPED_MAX_LENGTH = RELEASE_NOTE_VERSION_MAX_LENGTH - RELEASE_NOTE_VERSION_PREFIX.length;

/** The part of a whole version the box shows: everything after the v. */
function typedPart(version: string): string {
  return normaliseReleaseNoteVersionInput(version).slice(RELEASE_NOTE_VERSION_PREFIX.length);
}

/** The Version field: a fixed v beside a box for the numbers; emits the whole version. */
export function ReleaseNoteVersionInput({
  id,
  value,
  onChange,
  error,
  disabled,
}: {
  /** The input's id, which the label points at. */
  id: string;
  /** The whole version, v included - `v` alone when nothing has been typed. */
  value: string;
  /** Called with the whole, normalised version: always starts with exactly one v. */
  onChange: (next: string) => void;
  /** Shown under the field; the field is marked invalid while there is one. */
  error?: string | undefined;
  disabled?: boolean;
}): JSX.Element {
  const words = useMessages().admin.releaseNotes.versionInput;
  const input = useRef<HTMLInputElement>(null);
  /** Where the caret goes after a paste the component applied itself. */
  const pendingCaret = useRef<number | null>(null);

  const shown = typedPart(value);

  useLayoutEffect(() => {
    const caret = pendingCaret.current;
    if (caret === null || !input.current) return;
    pendingCaret.current = null;
    input.current.setSelectionRange(caret, caret);
  });

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData?.getData('text') ?? '';
    if (text.length === 0) return;

    event.preventDefault();
    const element = event.currentTarget;
    const current = element.value;
    const start = element.selectionStart ?? current.length;
    const end = element.selectionEnd ?? start;

    const next = normaliseReleaseNoteVersionInput(
      current.slice(0, start) + text + current.slice(end),
    );
    if (next === normaliseReleaseNoteVersionInput(current)) return;

    // The caret after the pasted text, counted in what survives normalising.
    pendingCaret.current = typedPart(current.slice(0, start) + text).length;
    onChange(next);
  };

  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  return (
    <div className={`field${error ? ' field-invalid' : ''}`}>
      <label htmlFor={id}>
        {words.label}
        <span aria-hidden="true"> *</span>
      </label>

      {/*
        A click on the v puts the caret at the start of the numbers: the v looks
        like the first character of the value, so a click on it should behave
        like a click there. mousedown, so the input never loses focus first.
      */}
      <div
        className="version-field"
        onMouseDown={(event) => {
          if (event.target === input.current || disabled) return;
          event.preventDefault();
          input.current?.focus();
          input.current?.setSelectionRange(0, 0);
        }}
      >
        <span className="version-prefix" aria-hidden="true">
          {RELEASE_NOTE_VERSION_PREFIX}
        </span>
        <input
          ref={input}
          id={id}
          type="text"
          value={shown}
          maxLength={TYPED_MAX_LENGTH}
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          aria-describedby={error ? `${hintId} ${errorId}` : hintId}
          aria-invalid={error ? true : undefined}
          onChange={(event) => onChange(normaliseReleaseNoteVersionInput(event.target.value))}
          onPaste={onPaste}
        />
      </div>

      <p className="field-hint" id={hintId}>
        {words.hint}
      </p>
      {error && (
        <p className="field-error" role="alert" id={errorId}>
          {error}
        </p>
      )}
    </div>
  );
}
